import axios from 'axios';
import Auth from '../../auth/auth';

export const accessAdminActions = {
  SUBJECT_SEARCH_SUCCESS: 'AC_ADMIN_SUBJECT_SEARCH_SUCCESS',
  SUBJECT_SELECTED: 'AC_ADMIN_SUBJECT_SELECTED',
  ASSIGNMENTS_LOAD_SUCCESS: 'AC_ADMIN_ASSIGNMENTS_LOAD_SUCCESS',
  EFFECTIVE_LOAD_SUCCESS: 'AC_ADMIN_EFFECTIVE_LOAD_SUCCESS',
  AUDIT_LOAD_SUCCESS: 'AC_ADMIN_AUDIT_LOAD_SUCCESS',
  ROLES_LOAD_SUCCESS: 'AC_ADMIN_ROLES_LOAD_SUCCESS',
  ROSTER_LOAD_SUCCESS: 'AC_ADMIN_ROSTER_LOAD_SUCCESS',
  ROSTER_FILTER_CHANGE: 'AC_ADMIN_ROSTER_FILTER_CHANGE',
  REVIEW_LOAD_SUCCESS: 'AC_ADMIN_REVIEW_LOAD_SUCCESS',
  AUDIT_FILTER_CHANGE: 'AC_ADMIN_AUDIT_FILTER_CHANGE',
  SCOPES_LOAD_SUCCESS: 'AC_ADMIN_SCOPES_LOAD_SUCCESS',
  // Slice 4c — mockup screen S7's activation half. It lives on THIS page rather than beside the
  // nomination it activates: Q7 puts activation on `platform_admin`, who typically has no context,
  // and `secured-layout.js` exempts only `/settings/access` from the context redirect.
  NOMINATIONS_LOAD_SUCCESS: 'AC_ADMIN_NOMINATIONS_LOAD_SUCCESS',
  ACTIVATE_FAIL: 'AC_ADMIN_ACTIVATE_FAIL',
  GRANT_DRAWER_OPEN: 'AC_ADMIN_GRANT_DRAWER_OPEN',
  GRANT_DRAWER_CLOSE: 'AC_ADMIN_GRANT_DRAWER_CLOSE',
  GRANT_SUBMIT_SUCCESS: 'AC_ADMIN_GRANT_SUBMIT_SUCCESS',
  GRANT_SUBMIT_FAIL: 'AC_ADMIN_GRANT_SUBMIT_FAIL',
  REVOKE_SUCCESS: 'AC_ADMIN_REVOKE_SUCCESS',
  MUTATION_FAIL: 'AC_ADMIN_MUTATION_FAIL',
};

const auth = new Auth();

// auth.getAccessToken(), not a detached `const {getAccessToken} = auth`: the destructured form
// only works today because the method body happens not to read `this` — one `this.` added to
// auth.js later would silently break every request this page makes.
function headers() {
  return {'Accept': 'application/json', 'Content-Type': 'application/json', 'x-access-token': auth.getAccessToken()};
}

// 4a's write path returns {ok:false, error, code} (docs/access/slice-4a-write-path-plan.md §4) —
// the drawer maps `code` to a specific message (§5); everything else falls back to `error`.
const errorPayload = (error) => {
  const data = error.response && error.response.data;
  return {error: (data && data.error) || String(error.message || error), code: data && data.code};
};

export function searchSubjects(q) {
  return function(dispatch) {
    return axios.get('/access/subjects', {params: {q}, headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.SUBJECT_SEARCH_SUCCESS, payload: res.data.subjects || []}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function loadAssignments(subjectId) {
  return function(dispatch) {
    return axios.get('/access/assignments', {params: {subjectId}, headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.ASSIGNMENTS_LOAD_SUCCESS, payload: res.data.assignments || []}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

/**
 * `GET /access/roster` — mockup screen S1. The page's own data, loaded on mount, so the admin home
 * shows the shape of access rather than an empty search box.
 */
export function loadRoster(filters) {
  return function(dispatch) {
    dispatch({type: accessAdminActions.ROSTER_FILTER_CHANGE, payload: filters || {}});
    return axios.get('/access/roster', {params: filters || {}, headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.ROSTER_LOAD_SUCCESS, payload: res.data}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

/**
 * `/explain`, not `/effective` — mockup screen S3 (slice 4b-2 §3.2).
 *
 * The plain endpoint answers *whether*; this answers *why* (which grant produced each pair, at a
 * named scope) and *why not*. It is a different endpoint rather than a change to the old one, so
 * 4a's `/effective/:subjectId` and any other caller keep working.
 */
export function loadEffective(subjectId) {
  return function(dispatch) {
    return axios.get(`/access/effective/${subjectId}/explain`, {headers: headers()})
        .then((res) => dispatch({
          type: accessAdminActions.EFFECTIVE_LOAD_SUCCESS,
          payload: {canDo: res.data.canDo || [], cannotDo: res.data.cannotDo || []},
        }))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function selectSubject(subject) {
  return function(dispatch) {
    // `null` closes the detail panel. Guarded rather than assumed: the roster's Close button is
    // the only caller that passes it, and reading `.id` off null is a blank page, not an error
    // anyone would see the cause of.
    dispatch({type: accessAdminActions.SUBJECT_SELECTED, payload: subject});
    if (!subject) return Promise.resolve();
    dispatch(loadAssignments(subject.id));
    return dispatch(loadEffective(subject.id));
  };
}

export function loadAudit(filters = {}) {
  return function(dispatch) {
    dispatch({type: accessAdminActions.AUDIT_FILTER_CHANGE, payload: filters});
    return axios.get('/access/audit', {params: filters, headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.AUDIT_LOAD_SUCCESS, payload: res.data.events || []}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function loadReview() {
  return function(dispatch) {
    return axios.get('/access/review', {headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.REVIEW_LOAD_SUCCESS, payload: res.data.findings || []}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function loadRoles() {
  return function(dispatch) {
    return axios.get('/access/roles', {headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.ROLES_LOAD_SUCCESS, payload: res.data.roles || []}))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function loadScopes(type) {
  return function(dispatch) {
    return axios.get('/access/scopes', {params: {type}, headers: headers()})
        .then((res) => dispatch({
          type: accessAdminActions.SCOPES_LOAD_SUCCESS,
          payload: {scopeType: type, options: res.data.scopes || []},
        }))
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

export function openGrantDrawer() {
  return {type: accessAdminActions.GRANT_DRAWER_OPEN};
}

export function closeGrantDrawer() {
  return {type: accessAdminActions.GRANT_DRAWER_CLOSE};
}

export function createAssignment(body, subjectId) {
  return function(dispatch) {
    return axios.post('/access/assignments', body, {headers: headers()})
        .then(() => {
          dispatch({type: accessAdminActions.GRANT_SUBMIT_SUCCESS});
          dispatch(loadAssignments(subjectId));
          dispatch(loadEffective(subjectId));
          dispatch(loadAudit());
          dispatch(loadRoster());
        })
        .catch((error) => dispatch({type: accessAdminActions.GRANT_SUBMIT_FAIL, payload: errorPayload(error)}));
  };
}

export function revokeAssignment(assignmentId, subjectId) {
  return function(dispatch) {
    return axios.delete(`/access/assignments/${assignmentId}`, {headers: headers()})
        .then(() => {
          dispatch({type: accessAdminActions.REVOKE_SUCCESS});
          dispatch(loadAssignments(subjectId));
          dispatch(loadEffective(subjectId));
          dispatch(loadAudit());
          dispatch(loadRoster());
        })
        .catch((error) => dispatch({type: accessAdminActions.MUTATION_FAIL, payload: errorPayload(error).error}));
  };
}

// ── Slice 4c — standing nominations (mockup S7's activation view) ────────────────────────────

export function loadNominations() {
  return function(dispatch) {
    axios.get('/access/nominations', {headers: headers()})
        .then((res) => dispatch({type: accessAdminActions.NOMINATIONS_LOAD_SUCCESS,
          payload: (res.data || {}).nominations || []}))
        // A 403 leaves the panel EMPTY rather than erroring. This route is gated on
        // `access.delegation:activate`, which Q7 gives to platform_admin alone — so for any other
        // super user the denial is the correct answer, not a failure of the page.
        .catch(() => dispatch({type: accessAdminActions.NOMINATIONS_LOAD_SUCCESS, payload: []}));
  };
}

export function activateNomination(nominationId, body) {
  return function(dispatch) {
    axios.post(`/access/nominations/${nominationId}/activate`, body, {headers: headers()})
        .then(() => {
          dispatch(loadNominations());
          // The audit panel is on the same screen, and an activation it does not show would make
          // the page contradict itself.
          dispatch(loadAudit());
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) ||
            String(error.message || error);
          dispatch({type: accessAdminActions.ACTIVATE_FAIL, payload: msg});
        });
  };
}
